import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";

export interface SpatialNavigationConfig {
  enabled: boolean;
  wrapAround: boolean;
  rememberLastFocus: boolean;
  focusRingVisible: boolean;
  debugMode: boolean;
}

export interface FocusableElement {
  id: string;
  element: HTMLElement;
  region: string;
  priority: number;
  disabled: boolean;
  bounds: DOMRect;
}

export interface SpatialNavigationContextType {
  config: SpatialNavigationConfig;
  updateConfig: (updates: Partial<SpatialNavigationConfig>) => void;
  registerElement: (element: FocusableElement) => void;
  unregisterElement: (id: string) => void;
  focusElement: (id: string) => void;
  getCurrentFocus: () => string | null;
  getFocusableElements: (region?: string) => FocusableElement[];
  navigateInDirection: (direction: "up" | "down" | "left" | "right") => boolean;
  setActiveRegion: (region: string) => void;
  getActiveRegion: () => string | null;
}

const SpatialNavigationContext =
  createContext<SpatialNavigationContextType | null>(null);

/**
 * Spatial navigation provider for 2D keyboard navigation
 */
export const SpatialNavigationProvider: React.FC<{
  children: React.ReactNode;
  config?: Partial<SpatialNavigationConfig>;
}> = ({ children, config: initialConfig = {} }) => {
  const [config, setConfig] = React.useState<SpatialNavigationConfig>({
    enabled: true,
    wrapAround: false,
    rememberLastFocus: true,
    focusRingVisible: true,
    debugMode: false,
    ...initialConfig,
  });

  const elementsRef = useRef<Map<string, FocusableElement>>(new Map());
  const currentFocusRef = useRef<string | null>(null);
  const activeRegionRef = useRef<string | null>(null);
  const lastFocusPerRegionRef = useRef<Map<string, string>>(new Map());

  /**
   * Update configuration
   */
  const updateConfig = useCallback(
    (updates: Partial<SpatialNavigationConfig>) => {
      setConfig((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  /**
   * Register a focusable element
   */
  const registerElement = useCallback(
    (element: FocusableElement) => {
      elementsRef.current.set(element.id, element);

      if (config.debugMode) {
        console.log("Registered spatial navigation element:", element.id);
      }
    },
    [config.debugMode],
  );

  /**
   * Unregister a focusable element
   */
  const unregisterElement = useCallback(
    (id: string) => {
      elementsRef.current.delete(id);

      if (currentFocusRef.current === id) {
        currentFocusRef.current = null;
      }

      if (config.debugMode) {
        console.log("Unregistered spatial navigation element:", id);
      }
    },
    [config.debugMode],
  );

  /**
   * Focus an element by ID
   */
  const focusElement = useCallback(
    (id: string) => {
      const element = elementsRef.current.get(id);
      if (!element || element.disabled) return;

      element.element.focus();
      currentFocusRef.current = id;

      // Remember last focus for this region
      if (config.rememberLastFocus && element.region) {
        lastFocusPerRegionRef.current.set(element.region, id);
      }

      if (config.debugMode) {
        console.log("Focused element:", id, "in region:", element.region);
      }
    },
    [config.rememberLastFocus, config.debugMode],
  );

  /**
   * Get currently focused element ID
   */
  const getCurrentFocus = useCallback(() => {
    return currentFocusRef.current;
  }, []);

  /**
   * Get focusable elements, optionally filtered by region
   */
  const getFocusableElements = useCallback(
    (region?: string): FocusableElement[] => {
      const elements = Array.from(elementsRef.current.values());
      return elements.filter(
        (el) => !el.disabled && (region === undefined || el.region === region),
      );
    },
    [],
  );

  /**
   * Set active region for navigation
   */
  const setActiveRegion = useCallback(
    (region: string) => {
      activeRegionRef.current = region;

      // Try to restore last focus in this region
      if (config.rememberLastFocus) {
        const lastFocus = lastFocusPerRegionRef.current.get(region);
        if (lastFocus && elementsRef.current.has(lastFocus)) {
          focusElement(lastFocus);
          return;
        }
      }

      // Otherwise, focus first element in region
      const regionElements = getFocusableElements(region);
      if (regionElements.length > 0) {
        const firstElement = regionElements.sort(
          (a, b) => b.priority - a.priority,
        )[0];
        focusElement(firstElement.id);
      }
    },
    [config.rememberLastFocus, focusElement, getFocusableElements],
  );

  /**
   * Get active region
   */
  const getActiveRegion = useCallback(() => {
    return activeRegionRef.current;
  }, []);

  /**
   * Calculate distance between two rectangles
   */
  const calculateDistance = useCallback(
    (rect1: DOMRect, rect2: DOMRect): number => {
      const centerX1 = rect1.left + rect1.width / 2;
      const centerY1 = rect1.top + rect1.height / 2;
      const centerX2 = rect2.left + rect2.width / 2;
      const centerY2 = rect2.top + rect2.height / 2;

      return Math.sqrt(
        Math.pow(centerX2 - centerX1, 2) + Math.pow(centerY2 - centerY1, 2),
      );
    },
    [],
  );

  /**
   * Check if element is in the specified direction from current element
   */
  const isInDirection = useCallback(
    (
      currentRect: DOMRect,
      targetRect: DOMRect,
      direction: "up" | "down" | "left" | "right",
    ): boolean => {
      const currentCenterX = currentRect.left + currentRect.width / 2;
      const currentCenterY = currentRect.top + currentRect.height / 2;
      const targetCenterX = targetRect.left + targetRect.width / 2;
      const targetCenterY = targetRect.top + targetRect.height / 2;

      const threshold = 10; // Pixels of tolerance

      switch (direction) {
        case "up":
          return targetCenterY < currentCenterY - threshold;
        case "down":
          return targetCenterY > currentCenterY + threshold;
        case "left":
          return targetCenterX < currentCenterX - threshold;
        case "right":
          return targetCenterX > currentCenterX + threshold;
        default:
          return false;
      }
    },
    [],
  );

  /**
   * Navigate in a specific direction
   */
  const navigateInDirection = useCallback(
    (direction: "up" | "down" | "left" | "right"): boolean => {
      if (!config.enabled || !currentFocusRef.current) return false;

      const currentElement = elementsRef.current.get(currentFocusRef.current);
      if (!currentElement) return false;

      // Get elements in the same region or all elements if no region is active
      const region = activeRegionRef.current || currentElement.region;
      const candidates = getFocusableElements(region);

      // Filter candidates by direction
      const validCandidates = candidates.filter((candidate) => {
        if (candidate.id === currentElement.id) return false;
        return isInDirection(
          currentElement.bounds,
          candidate.bounds,
          direction,
        );
      });

      if (validCandidates.length === 0) {
        // Handle wrap around if enabled
        if (config.wrapAround) {
          const allCandidates = candidates.filter(
            (c) => c.id !== currentElement.id,
          );
          if (allCandidates.length > 0) {
            // Find element on opposite side
            let wrapTarget: FocusableElement;
            switch (direction) {
              case "up":
                wrapTarget = allCandidates.reduce((bottom, candidate) =>
                  candidate.bounds.top > bottom.bounds.top ? candidate : bottom,
                );
                break;
              case "down":
                wrapTarget = allCandidates.reduce((top, candidate) =>
                  candidate.bounds.top < top.bounds.top ? candidate : top,
                );
                break;
              case "left":
                wrapTarget = allCandidates.reduce((right, candidate) =>
                  candidate.bounds.left > right.bounds.left ? candidate : right,
                );
                break;
              case "right":
                wrapTarget = allCandidates.reduce((left, candidate) =>
                  candidate.bounds.left < left.bounds.left ? candidate : left,
                );
                break;
              default:
                return false;
            }
            focusElement(wrapTarget.id);
            return true;
          }
        }
        return false;
      }

      // Find closest candidate
      const closest = validCandidates.reduce((best, candidate) => {
        const distance = calculateDistance(
          currentElement.bounds,
          candidate.bounds,
        );
        const bestDistance = calculateDistance(
          currentElement.bounds,
          best.bounds,
        );

        // Prefer higher priority elements if distances are similar
        if (Math.abs(distance - bestDistance) < 50) {
          return candidate.priority > best.priority ? candidate : best;
        }

        return distance < bestDistance ? candidate : best;
      });

      focusElement(closest.id);
      return true;
    },
    [
      config.enabled,
      config.wrapAround,
      getFocusableElements,
      isInDirection,
      calculateDistance,
      focusElement,
    ],
  );

  /**
   * Handle keyboard events for spatial navigation
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!config.enabled) return;

      // Only handle arrow keys
      const arrowKeyMap: Record<string, "up" | "down" | "left" | "right"> = {
        ArrowUp: "up",
        ArrowDown: "down",
        ArrowLeft: "left",
        ArrowRight: "right",
      };

      const direction = arrowKeyMap[event.key];
      if (!direction) return;

      // Don't interfere if user is typing in form elements
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const handled = navigateInDirection(direction);
      if (handled) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [config.enabled, navigateInDirection]);

  /**
   * Update element bounds when window resizes
   */
  useEffect(() => {
    const updateBounds = () => {
      for (const [id, element] of elementsRef.current) {
        const bounds = element.element.getBoundingClientRect();
        elementsRef.current.set(id, { ...element, bounds });
      }
    };

    window.addEventListener("resize", updateBounds);
    return () => window.removeEventListener("resize", updateBounds);
  }, []);

  const contextValue: SpatialNavigationContextType = {
    config,
    updateConfig,
    registerElement,
    unregisterElement,
    focusElement,
    getCurrentFocus,
    getFocusableElements,
    navigateInDirection,
    setActiveRegion,
    getActiveRegion,
  };

  return (
    <SpatialNavigationContext.Provider value={contextValue}>
      {children}
    </SpatialNavigationContext.Provider>
  );
};

/**
 * Hook to use spatial navigation context
 */
export function useSpatialNavigation() {
  const context = useContext(SpatialNavigationContext);
  if (!context) {
    throw new Error(
      "useSpatialNavigation must be used within a SpatialNavigationProvider",
    );
  }
  return context;
}
